import axios from "axios";
import {DolphinAPIClientProvider} from "@amzn/dolphin-web-framework";
import {getNotificationServiceBaseURL, getDolphinAuthBaseURL, getDolphinAuthAPIKey} from "./BaseUrlUtils";

/**
 * Returns the framework-managed Axios client for the NotificationService API.
 *
 * Shepherd CWE-306 — the previous implementation here used a raw `axios.create({...})`
 * which sent every NotificationService request without any auth header. The
 * NotificationService API Gateway in gamma now runs a Lambda authorizer in ENFORCE mode
 * and rejects requests that lack `x-amz-access-token`; without this fix, every PWA call
 * to /createHelpRequest (the only NotificationService endpoint actually called from this
 * app today) would 401 the moment the authorizer is enabled in any region.
 *
 * `DolphinAPIClientProvider` is the same client the rest of the framework uses to talk to
 * authenticated Dolphin services. It carries an `authInterceptor` that attaches
 * `x-amz-access-token` automatically — emitting a MAASK SSO JWT for users enrolled in
 * MAASK and falling back to a legacy MAP token (`Atna|...`) otherwise. Both formats are
 * understood by the NotificationService authorizer.
 *
 * Because `generateDolphinAPIClient` is async (it sets up the auth interceptor on first
 * use), the factory is now async too. Callers (`axiosGET` / `axiosPOST`) await it.
 *
 * The cached client is keyed by `baseURL`, so toggling between regions or stages
 * regenerates rather than reusing a stale client.
 */
export async function getNotificationServiceClient() {
    if (!DolphinAPIClientProvider.dolphinAPIClient
        || DolphinAPIClientProvider.dolphinAPIClient.defaults.baseURL !== getNotificationServiceBaseURL()) {
        await DolphinAPIClientProvider.generateDolphinAPIClient(getNotificationServiceBaseURL(), 20000);
    }
    return DolphinAPIClientProvider.dolphinAPIClient;
}

export function getDolphinAuthClient() {
    const dolphinAPIClient = axios.create({
        baseURL: getDolphinAuthBaseURL(),
        timeout: 20000,
        headers: {'X-API-Key': getDolphinAuthAPIKey()}
    });

    return dolphinAPIClient;
}

export async function axiosGET(path, params) {
    try {
        const client = await getNotificationServiceClient();
        const response = await client.get(path, params);
        return response;
    } catch(error) {
        console.error("GET call to path->",path, " errored out: ", error)
        throw error;
    }
}

export async function axiosPOST(path, requestData) {
    try {
        const client = await getNotificationServiceClient();
        const response = await client.post(path, requestData);
        return response;
    } catch(error) {
        console.error("POST call to path->",path, " errored out: ", error)
        throw error;
    }
}

export async function axiosDolphinAuthGET(path, params) {
    try {
        const response = await getDolphinAuthClient().get(path, params);
        return response;
    } catch(error) {
        console.error("GET call to path->",path, " errored out: ", error)
        throw error;
    }
}
